const inlineTags = new Set([
  "A",
  "ABBR",
  "B",
  "BDI",
  "BDO",
  "CITE",
  "CODE",
  "DEL",
  "EM",
  "FONT",
  "I",
  "INS",
  "KBD",
  "LABEL",
  "MARK",
  "Q",
  "S",
  "SAMP",
  "SMALL",
  "SPAN",
  "STRONG",
  "SUB",
  "SUP",
  "TIME",
  "U",
  "VAR",
]);
const protectedSpace = "\uE000";

const isInlineElement = (node: Node | null): node is Element =>
  node?.nodeType === Node.ELEMENT_NODE &&
  inlineTags.has((node as Element).tagName);

export const isMicrosoftWordHtml = (html: string) =>
  /class=["']*Mso(?:Normal|List)/i.test(html) ||
  /content=["']*Word\.Document/i.test(html) ||
  /\bmso-/i.test(html);

/**
 * Word wraps the clipboard HTML at ~76 columns and writes CRLF in place of the
 * space it broke the line at. SunEditor does this same replacement itself for
 * MS Word data before cleanHTML, but onPaste starts from the raw clipboard HTML
 * and htmlCompress drops every "\n" — so without it words glue together at
 * every wrap point (9243, second round).
 */
const restoreWordLineWraps = (html: string) => html.replace(/\r\n|\r|\n/g, " ");

const endsWithSpace = (node: Node | null) =>
  /\s$/.test(node?.textContent ?? "");
const startsWithSpace = (node: Node | null) =>
  /^\s/.test(node?.textContent ?? "");

/**
 * SunEditor's HTML compressor removes whitespace-only text nodes between
 * tags. Word commonly puts separators in their own span/run, so protect those
 * spaces as HTML entities before passing the HTML through SunEditor's cleaner.
 */
export const preserveInlinePasteWhitespace = (html: string) => {
  const parsed = new DOMParser().parseFromString(
    restoreWordLineWraps(html),
    "text/html"
  );
  const walker = parsed.createTreeWalker(parsed.body, NodeFilter.SHOW_TEXT);
  const whitespaceNodes: Text[] = [];

  while (walker.nextNode()) {
    whitespaceNodes.push(walker.currentNode as Text);
  }

  whitespaceNodes.forEach((textNode) => {
    if (!/^[\t\n\f\r ]+$/.test(textNode.data)) {
      return;
    }

    const parent = textNode.parentElement;
    const betweenInlineElements =
      isInlineElement(textNode.previousSibling) &&
      isInlineElement(textNode.nextSibling);

    if (parent && (inlineTags.has(parent.tagName) || betweenInlineElements)) {
      // The neighbouring run already carries the space — a second one would
      // double it after the line wrap above turned into a space too.
      textNode.data =
        endsWithSpace(textNode.previousSibling) ||
        startsWithSpace(textNode.nextSibling)
          ? ""
          : protectedSpace;
    }
  });

  // Keep a regular, breakable space after SunEditor parses the HTML entity.
  return parsed.body.innerHTML.replaceAll(protectedSpace, "&#32;");
};
